import { Component, OnInit, QueryList, ViewChildren } from '@angular/core';
import {
  GridDataResult,
  PagerSettings,
  DataStateChangeEvent,
} from '@progress/kendo-angular-grid';
import {
  LoaderService,
  MessageService,
  ColumnOptions,
  ColumnTemplateDirective,
  GridState,
  ValidationResultService,
  GridStateCompositeFilterDescriptor,
  NamedEntity,
} from '@shared';
import { GlobalEventsService, Restaurant, RestaurantService } from '@shared-data';
import { GridRowRestaurant } from '../../models';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { debounceTime, distinctUntilChanged, Subject, tap } from 'rxjs';

@Component({
  selector: 'app-restaurant',
  templateUrl: './restaurant.component.html',
  styleUrls: ['./restaurant.component.scss'],
})
export class RestaurantComponent implements OnInit {
  restaurants: GridDataResult = { data: [], total: 0 };
  pageSize = 10;
  state = new GridState(this.pageSize);
  columns: ColumnOptions[] = [];
  reportingUnits: GridDataResult = { data: [], total: 0 };
  pagerSettings: PagerSettings = {
    buttonCount: 3,
    info: true,
    type: 'numeric',
    previousNext: true,
  };
  selectedIndex: number = 0;
  @ViewChildren(ColumnTemplateDirective)
  columnTemplates!: QueryList<ColumnTemplateDirective>;
  restaurantForm!: FormGroup;
  reportingUnitSearch: Subject<string> = new Subject<string>();

  selectedConcept: NamedEntity | null = null;

  constructor(
    private restaurantService: RestaurantService,
    private messageService: MessageService,
    private loaderService: LoaderService,
    private formBuilder: FormBuilder,
    private validationErrorService: ValidationResultService,
    private globalEventsService: GlobalEventsService,
  ) {
    this.columns = new GridRowRestaurant().getColumns();
  }

  ngOnInit(): void {
    this.setForm();
    this.subscribeToReportingUnitSearch();
    this.subscribeToConceptChanges();
  }

  subscribeToConceptChanges() {
    this.globalEventsService.selectedConcept$.subscribe((concept) => {
        this.selectedConcept = concept;
        this.setDefaultState();
    });
  }

  setForm() {
    this.restaurantForm = this.formBuilder.group({
      restaurantNumber: [null, Validators.required],
      selectedReportingUnit: [null, Validators.required],
      parToken: [null],
    });
  }

  onDataStateChanged(event: DataStateChangeEvent): void {
    this.state = event as GridState;
    this.fetchRestaurants(this.state);
  }

  setDefaultState(): void {
    this.state = new GridState(this.pageSize);
    this.state.filter = this.state.filter?.and({
      field: 'conceptKey',
      operator: 'eq',
      value: this.selectedConcept?.id,
    });

    this.fetchRestaurants(this.state);
    this.restaurantForm.reset();
  }

  fetchRestaurants(state: GridState): void {
    this.loaderService.show();

    if (state.skip !== undefined) this.state.skip = state.skip;
    if (state.take !== undefined) this.state.take = state.take;
    if (state.sort?.length) this.state.sort = state.sort;

    this.restaurantService.getAllRestaurants(this.state).subscribe({
      next: (result: GridDataResult) => {
        this.restaurants = result;
        this.loaderService.hide();
      },
      error: (error) => {
        this.messageService.error('Failed to load restaurants');
        var validationError = error?.error;
        if (validationError) {
          this.validationErrorService.show(validationError);
        }
        this.loaderService.hide();
      },
    });
  }

  saveRestaurant(): void {
    if (!this.restaurantForm.get('restaurantNumber')?.valid) {
      this.messageService.info('Restaurant Number is required');
      return;
    }
    if (!this.restaurantForm.get('selectedReportingUnit')?.valid) {
      this.messageService.info('Reporting Unit is required');
      return;
    }
    const newRestaurant = new Restaurant(
      '0',
      this.restaurantForm.get('restaurantNumber')?.value,
      this.restaurantForm.get('parToken')?.value || undefined,
      false,
    );
    newRestaurant.reportingUnit = this.restaurantForm.get(
      'selectedReportingUnit',
    )?.value?.ruNo;

    this.loaderService.show();
    this.restaurantService.createRestaurant(newRestaurant).subscribe({
      next: () => {
        this.loaderService.hide();
        this.messageService.success('Restaurant saved successfully');
        this.setDefaultState();
      },
      error: (error) => {
        this.messageService.error('Failed to save restaurant');
        var validationError = error?.error;
        if (validationError) {
          this.validationErrorService.show(validationError);
        }
        this.loaderService.hide();
      },
    });
  }

  onParTokenEdit(dataItem: any): void {
    dataItem._onClick = !dataItem._onClick;
    if (
      dataItem._onClick ||
      dataItem.parToken == null ||
      dataItem.parToken.trim() === ''
    )
      return;
    this.restaurantService
      .updateParToken(dataItem.id, dataItem.parToken)
      .subscribe({
        next: () => {
          this.messageService.success('Par Token updated successfully');
        },
        error: (error) => {
          this.messageService.error('Failed to update Par Token');
          var validationError = error?.error;
          if (validationError) {
            this.validationErrorService.show(validationError);
          }
        },
      });
  }

  fetchReportingUnits(state: GridState): void {
    this.loaderService.show();
    this.restaurantService.getReportingUnits(state).subscribe({
      next: (result: GridDataResult) => {
        this.reportingUnits = result;
        this.loaderService.hide();
      },
      error: (error) => {
        this.messageService.error('Failed to load reporting units');
        var validationError = error?.error;
        if (validationError) {
          this.validationErrorService.show(validationError);
        }
        this.loaderService.hide();
      },
    });
  }

  updateReportingUnitSearch(value: string) {
    this.reportingUnitSearch.next(value);
  }

  subscribeToReportingUnitSearch() {
    this.reportingUnitSearch
      .pipe(
        debounceTime(300),
        distinctUntilChanged(),
        tap((searchText) => {
          this.onReportingUnitFilter(searchText);
        }),
      )
      .subscribe();
  }

  onReportingUnitFilter(value: string): void {
    const state = new GridState();
    state.filter = new GridStateCompositeFilterDescriptor().and({
      field: 'ruNm',
      operator: 'contains',
      value: value,
    }).and({
      field: 'conceptKey',
      operator: 'eq',
      value: this.selectedConcept?.id,
    });
    
    this.fetchReportingUnits(state);
  }
}
